import Box from '@quintoandar/cozy-core/Box';
import Card from '@quintoandar/cozy-core/Card';
import CircularProgress from '@quintoandar/cozy-core/CircularProgress';
import Typography from '@quintoandar/cozy-core/Typography';
import PropTypes from 'prop-types';
import { React, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { StatusCircle } from './StatusCircle.js';
import { UPLOAD_STATUS } from './constants';
import messages from './messages.js';
import { useGetVimeoStatus } from './useGetVimeoStatus';

export const VimeoPoller = ({ houseId, currentStatus, onError, onSuccess }) => {
  const intl = useIntl();
  const isQueryEnabled = currentStatus === UPLOAD_STATUS.POLLING && Boolean(houseId);

  const {
    isError: errorPollingStatus,
    isSuccess: statusIsRetrieved,
    data: vimeoCompletedStatus,
    error,
  } = useGetVimeoStatus(houseId, isQueryEnabled);

  useEffect(() => {
    if (statusIsRetrieved && vimeoCompletedStatus) {
      onSuccess();
    }
  }, [statusIsRetrieved]);

  useEffect(() => {
    if (errorPollingStatus) {
      onError(error);
    }
  }, [errorPollingStatus]);

  const waitingToPoll = currentStatus === UPLOAD_STATUS.UPLOADING;
  const isPolling = currentStatus === UPLOAD_STATUS.POLLING;
  const pollingHasFailed = currentStatus === UPLOAD_STATUS.ERROR_POLLING;
  const hasPolledEffectively = currentStatus === UPLOAD_STATUS.SUCCESS;

  const getTitleMessage = () => {
    if (pollingHasFailed) {
      return intl.formatMessage(messages.errorPolling);
    }
    if (waitingToPoll) {
      return intl.formatMessage(messages.waitingToPoll);
    }
    if (hasPolledEffectively) {
      return 'ok';
    }

    return intl.formatMessage(messages.pollingVideo);
  };

  if (currentStatus === UPLOAD_STATUS.UNSELECTED_FILE || currentStatus === UPLOAD_STATUS.ERROR_UPLOADING) {
    return null;
  }

  return (<Card>
    <Box display="flex" alignItems="center" width="100%" minHeight="40px">
      <Box
        height="100%"
        display="flex"
        alignItems="flex-start"
        justifyContent="start"
      >
        <Box>{(isPolling) && <CircularProgress size={25} />}</Box>
        {waitingToPoll && <StatusCircle size={20} color="#dedede" />}
        {hasPolledEffectively && <StatusCircle size={20} color="#8ccf59" />}
        {pollingHasFailed && <StatusCircle size={20} color="#c13c37" />}
      </Box>
      <Box ml={3} width="100%" minHeight="100%">
        <Box display="flex" alignItems="flex-start">
          <Typography kind={waitingToPoll ? 'disabled' : 'default'} align="left" level="bodyStrong">
            {getTitleMessage()}
          </Typography>
        </Box>
        {isPolling && (
          <Box mt={1} width="80%">
            <Typography level="caption">
              <FormattedMessage {...messages.pollingVideoDescription} />
            </Typography>
          </Box>
        )}
        {waitingToPoll && (
          <Box mt={1} width="80%">
            <Typography kind="disabled"level="caption">
              <FormattedMessage {...messages.waitingToPollDescription} />
            </Typography>
          </Box>
        )}
        {pollingHasFailed && (
          <Box mt={1} width="80%">
            <Typography level="caption">
              <FormattedMessage {...messages.errorPollingDescription} />
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  </Card>);
};

VimeoPoller.propTypes = {
  houseId: PropTypes.string,
  currentStatus: PropTypes.string,
  onError: PropTypes.func,
  onSuccess: PropTypes.func,
};
